import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { Trash01Icon } from '@untitled-theme/icons-react'
import { useEffect } from 'react'
import type { Control, FieldErrors, UseFormSetValue } from 'react-hook-form'
import { Controller, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  getArrayFieldErrorMessage,
  getFieldErrorMessage,
} from '@/features/isfp-upload/utils/errorHelpers'
import type {
  DHWGeneratorType,
  EnergySource,
  HeatingGeneratorType,
  ISFPExtractedData,
} from '@/shared/types/isfp.types'
import {
  getCompatibleDHWEnergySources,
  getCompatibleDHWGenerators,
  getCompatibleEnergySources,
  getCompatibleHeatingGenerators,
} from '@/shared/utils/heatingSystemEnergySources'
import { energySourceLabels, type SelectOption } from '@/shared/utils/isfpEnumLabels'

import type { ISFPUploadFormProps } from './ISFPUploadForm.types'

/**
 * Component for heating generation field that dynamically filters energy sources
 * based on the selected generator type
 */
const HeatingGenerationField = ({
  control,
  errors,
  index,
  heatingGeneratorTypeOptions,
  setValue,
  onRemove,
  onFieldFocus,
}: {
  control: Control<ISFPExtractedData>
  errors: FieldErrors<ISFPExtractedData>
  index: number
  heatingGeneratorTypeOptions: SelectOption<HeatingGeneratorType>[]
  setValue: UseFormSetValue<ISFPExtractedData>
  onRemove: () => void
  onFieldFocus: (path: string) => void
}) => {
  // Watch the generator type and energy source for this specific field
  const generatorType = useWatch({
    control,
    name: `systemTechnology.heatingGeneration.${index}.generatorType`,
  }) as HeatingGeneratorType | undefined

  const currentEnergySource = useWatch({
    control,
    name: `systemTechnology.heatingGeneration.${index}.energySource`,
  }) as EnergySource | undefined

  // Get all generator type options with compatibility info
  const heatingGeneratorOptionsWithCompatibility = heatingGeneratorTypeOptions.map(option => ({
    ...option,
    isCompatible: currentEnergySource
      ? getCompatibleHeatingGenerators(currentEnergySource).includes(option.value)
      : true,
  }))

  // Get all energy source options with compatibility info
  const allEnergySourceOptions = Object.entries(energySourceLabels).map(([value, label]) => ({
    value: value as EnergySource,
    label,
    isCompatible: generatorType
      ? getCompatibleEnergySources(generatorType).includes(value as EnergySource)
      : true,
  }))

  // Clear generator type when incompatible energy source is selected
  useEffect(() => {
    if (currentEnergySource && generatorType) {
      const compatibleGenerators = getCompatibleHeatingGenerators(currentEnergySource)
      if (!compatibleGenerators.includes(generatorType)) {
        // Clear the field - undefined will be converted to empty string by the Select component
        setValue(
          `systemTechnology.heatingGeneration.${index}.generatorType`,
          undefined as unknown as HeatingGeneratorType,
          {
            shouldValidate: true,
          }
        )
        toast.error(
          `Der Heizungstyp wurde zurückgesetzt, da er nicht mit der ausgewählten Energiequelle kompatibel ist.`,
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEnergySource, index, setValue])

  // Clear energy source when incompatible generator type is selected
  useEffect(() => {
    if (generatorType && currentEnergySource) {
      const compatibleSources = getCompatibleEnergySources(generatorType)
      if (!compatibleSources.includes(currentEnergySource)) {
        // Clear the field - undefined will be converted to empty string by the Select component
        setValue(
          `systemTechnology.heatingGeneration.${index}.energySource`,
          undefined as unknown as EnergySource,
          {
            shouldValidate: true,
          }
        )
        toast.error(
          `Die Energiequelle wurde zurückgesetzt, da sie nicht mit dem ausgewählten Heizungstyp kompatibel ist.`,
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatorType, index, setValue])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '16px',
        padding: '16px',
        border: '1px solid #E1E1E4',
        borderRadius: '8px',
      }}
    >
      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <Controller
          name={`systemTechnology.heatingGeneration.${index}.generatorType`}
          control={control}
          render={({ field }) => (
            <FormControl
              size="small"
              sx={{ flex: 1 }}
              error={!!errors.systemTechnology?.heatingGeneration?.[index]?.generatorType}
            >
              <InputLabel>Typ</InputLabel>
              <Select
                {...field}
                label="Typ"
                value={generatorType ?? ''}
                onFocus={() => onFieldFocus(`systemTechnology.heatingGeneration.${index}`)}
              >
                {heatingGeneratorOptionsWithCompatibility.map(option => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      color: option.isCompatible ? 'inherit' : 'text.disabled',
                      opacity: option.isCompatible ? 1 : 0.5,
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.systemTechnology?.heatingGeneration?.[index]?.generatorType && (
                <FormHelperText>
                  {getArrayFieldErrorMessage(
                    errors,
                    'systemTechnology.heatingGeneration',
                    index,
                    'generatorType'
                  )}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
        <Controller
          name={`systemTechnology.heatingGeneration.${index}.energySource`}
          control={control}
          render={({ field }) => (
            <FormControl
              size="small"
              sx={{ flex: 1 }}
              error={!!errors.systemTechnology?.heatingGeneration?.[index]?.energySource}
            >
              <InputLabel>Energiequelle</InputLabel>
              <Select
                {...field}
                label="Energiequelle"
                value={currentEnergySource ?? ''}
                onFocus={() => onFieldFocus(`systemTechnology.heatingGeneration.${index}`)}
              >
                {allEnergySourceOptions.map(option => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      color: option.isCompatible ? 'inherit' : 'text.disabled',
                      opacity: option.isCompatible ? 1 : 0.5,
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.systemTechnology?.heatingGeneration?.[index]?.energySource && (
                <FormHelperText>
                  {getArrayFieldErrorMessage(
                    errors,
                    'systemTechnology.heatingGeneration',
                    index,
                    'energySource'
                  )}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
        <IconButton onClick={onRemove} size="small">
          <Trash01Icon style={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Controller
          name={`systemTechnology.heatingGeneration.${index}.constructionYear`}
          control={control}
          render={({ field: { onChange, value, ...field } }) => (
            <TextField
              {...field}
              label="Baujahr"
              type="number"
              size="small"
              value={value ?? ''}
              onChange={e => onChange(parseInt(e.target.value, 10))}
              onFocus={() => onFieldFocus(`systemTechnology.heatingGeneration.${index}`)}
              error={!!errors.systemTechnology?.heatingGeneration?.[index]?.constructionYear}
              helperText={getArrayFieldErrorMessage(
                errors,
                'systemTechnology.heatingGeneration',
                index,
                'constructionYear'
              )}
              slotProps={{ htmlInput: { step: 1 } }}
              sx={{ flex: 1 }}
            />
          )}
        />
        <Controller
          name={`systemTechnology.heatingGeneration.${index}.coverageInPct`}
          control={control}
          render={({ field: { onChange, value, ...field } }) => (
            <TextField
              {...field}
              label="Abdeckung (%)"
              type="number"
              size="small"
              value={value ?? ''}
              onChange={e => onChange(parseFloat(e.target.value))}
              onFocus={() => onFieldFocus(`systemTechnology.heatingGeneration.${index}`)}
              error={!!errors.systemTechnology?.heatingGeneration?.[index]?.coverageInPct}
              helperText={getArrayFieldErrorMessage(
                errors,
                'systemTechnology.heatingGeneration',
                index,
                'coverageInPct'
              )}
              slotProps={{ htmlInput: { step: 'any', min: 0, max: 100 } }}
              sx={{ flex: 1 }}
            />
          )}
        />
      </Box>
    </Box>
  )
}

/**
 * Component for DHW generation field that dynamically filters energy sources
 * based on the selected generator type
 */
const DHWGenerationField = ({
  control,
  errors,
  index,
  dhwGeneratorTypeOptions,
  setValue,
  onRemove,
  onFieldFocus,
}: {
  control: Control<ISFPExtractedData>
  errors: FieldErrors<ISFPExtractedData>
  index: number
  dhwGeneratorTypeOptions: SelectOption<DHWGeneratorType>[]
  setValue: UseFormSetValue<ISFPExtractedData>
  onRemove: () => void
  onFieldFocus: (path: string) => void
}) => {
  // Watch the generator type and energy source for this specific field
  const generatorType = useWatch({
    control,
    name: `systemTechnology.drinkingHotWaterGeneration.${index}.generatorType`,
  }) as DHWGeneratorType | undefined

  const currentEnergySource = useWatch({
    control,
    name: `systemTechnology.drinkingHotWaterGeneration.${index}.energySource`,
  }) as EnergySource | undefined

  // Get all generator type options with compatibility info
  const dhwGeneratorOptionsWithCompatibility = dhwGeneratorTypeOptions.map(option => ({
    ...option,
    isCompatible: currentEnergySource
      ? getCompatibleDHWGenerators(currentEnergySource).includes(option.value)
      : true,
  }))

  // Get all energy source options with compatibility info
  const allEnergySourceOptions = Object.entries(energySourceLabels).map(([value, label]) => ({
    value: value as EnergySource,
    label,
    isCompatible: generatorType
      ? getCompatibleDHWEnergySources(generatorType).includes(value as EnergySource)
      : true,
  }))

  // Clear generator type when incompatible energy source is selected
  useEffect(() => {
    if (currentEnergySource && generatorType) {
      const compatibleGenerators = getCompatibleDHWGenerators(currentEnergySource)
      if (!compatibleGenerators.includes(generatorType)) {
        // Clear the field - undefined will be converted to empty string by the Select component
        setValue(
          `systemTechnology.drinkingHotWaterGeneration.${index}.generatorType`,
          undefined as unknown as DHWGeneratorType,
          {
            shouldValidate: true,
          }
        )
        toast.error(
          `Der Warmwassertyp wurde zurückgesetzt, da er nicht mit der ausgewählten Energiequelle kompatibel ist.`,
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEnergySource, index, setValue])

  // Clear energy source when incompatible generator type is selected
  useEffect(() => {
    if (generatorType && currentEnergySource) {
      const compatibleSources = getCompatibleDHWEnergySources(generatorType)
      if (!compatibleSources.includes(currentEnergySource)) {
        // Clear the field - undefined will be converted to empty string by the Select component
        setValue(
          `systemTechnology.drinkingHotWaterGeneration.${index}.energySource`,
          undefined as unknown as EnergySource,
          {
            shouldValidate: true,
          }
        )
        toast.error(
          `Die Energiequelle wurde zurückgesetzt, da sie nicht mit dem ausgewählten Warmwassertyp kompatibel ist.`,
          {
            position: 'top-right',
            autoClose: 4000,
          }
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatorType, index, setValue])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '16px',
        padding: '16px',
        border: '1px solid #E1E1E4',
        borderRadius: '8px',
      }}
    >
      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <Controller
          name={`systemTechnology.drinkingHotWaterGeneration.${index}.generatorType`}
          control={control}
          render={({ field }) => (
            <FormControl
              size="small"
              sx={{ flex: 1 }}
              error={!!errors.systemTechnology?.drinkingHotWaterGeneration?.[index]?.generatorType}
            >
              <InputLabel>Typ</InputLabel>
              <Select
                {...field}
                label="Typ"
                value={generatorType ?? ''}
                onFocus={() => onFieldFocus(`systemTechnology.drinkingHotWaterGeneration.${index}`)}
              >
                {dhwGeneratorOptionsWithCompatibility.map(option => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      color: option.isCompatible ? 'inherit' : 'text.disabled',
                      opacity: option.isCompatible ? 1 : 0.5,
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.systemTechnology?.drinkingHotWaterGeneration?.[index]?.generatorType && (
                <FormHelperText>
                  {getArrayFieldErrorMessage(
                    errors,
                    'systemTechnology.drinkingHotWaterGeneration',
                    index,
                    'generatorType'
                  )}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
        <Controller
          name={`systemTechnology.drinkingHotWaterGeneration.${index}.energySource`}
          control={control}
          render={({ field }) => (
            <FormControl
              size="small"
              sx={{ flex: 1 }}
              error={!!errors.systemTechnology?.drinkingHotWaterGeneration?.[index]?.energySource}
            >
              <InputLabel>Energiequelle</InputLabel>
              <Select
                {...field}
                label="Energiequelle"
                value={currentEnergySource ?? ''}
                onFocus={() => onFieldFocus(`systemTechnology.drinkingHotWaterGeneration.${index}`)}
              >
                {allEnergySourceOptions.map(option => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      color: option.isCompatible ? 'inherit' : 'text.disabled',
                      opacity: option.isCompatible ? 1 : 0.5,
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.systemTechnology?.drinkingHotWaterGeneration?.[index]?.energySource && (
                <FormHelperText>
                  {getArrayFieldErrorMessage(
                    errors,
                    'systemTechnology.drinkingHotWaterGeneration',
                    index,
                    'energySource'
                  )}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
        <IconButton onClick={onRemove} size="small">
          <Trash01Icon style={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Controller
          name={`systemTechnology.drinkingHotWaterGeneration.${index}.constructionYear`}
          control={control}
          render={({ field: { onChange, value, ...field } }) => (
            <TextField
              {...field}
              label="Baujahr"
              type="number"
              size="small"
              value={value ?? ''}
              onChange={e => onChange(parseInt(e.target.value, 10))}
              onFocus={() => onFieldFocus(`systemTechnology.drinkingHotWaterGeneration.${index}`)}
              error={
                !!errors.systemTechnology?.drinkingHotWaterGeneration?.[index]?.constructionYear
              }
              helperText={getArrayFieldErrorMessage(
                errors,
                'systemTechnology.drinkingHotWaterGeneration',
                index,
                'constructionYear'
              )}
              slotProps={{ htmlInput: { step: 1 } }}
              sx={{ flex: 1 }}
            />
          )}
        />
        <Controller
          name={`systemTechnology.drinkingHotWaterGeneration.${index}.coverageInPct`}
          control={control}
          render={({ field: { onChange, value, ...field } }) => (
            <TextField
              {...field}
              label="Abdeckung (%)"
              type="number"
              size="small"
              value={value ?? ''}
              onChange={e => onChange(parseFloat(e.target.value))}
              onFocus={() => onFieldFocus(`systemTechnology.drinkingHotWaterGeneration.${index}`)}
              error={!!errors.systemTechnology?.drinkingHotWaterGeneration?.[index]?.coverageInPct}
              helperText={getArrayFieldErrorMessage(
                errors,
                'systemTechnology.drinkingHotWaterGeneration',
                index,
                'coverageInPct'
              )}
              slotProps={{ htmlInput: { step: 'any', min: 0, max: 100 } }}
              sx={{ flex: 1 }}
            />
          )}
        />
      </Box>
    </Box>
  )
}

const ISFPUploadForm = ({ controller, metadata, onFieldFocus }: ISFPUploadFormProps) => {
  const {
    control,
    errors,
    setValue,
    trigger,
    // Substance field arrays
    exteriorWallFields,
    appendExteriorWall,
    removeExteriorWall,
    roofFields,
    appendRoof,
    removeRoof,
    topFloorCeilingFields,
    appendTopFloorCeiling,
    removeTopFloorCeiling,
    basementCeilingFields,
    appendBasementCeiling,
    removeBasementCeiling,
    floorPlateFields,
    appendFloorPlate,
    removeFloorPlate,
    basementExteriorWallFields,
    appendBasementExteriorWall,
    removeBasementExteriorWall,
    windowFields,
    appendWindow,
    removeWindow,
    doorFields,
    appendDoor,
    removeDoor,
    roofWindowFields,
    appendRoofWindow,
    removeRoofWindow,
    // System technology field arrays
    heatingGenerationFields,
    appendHeatingGeneration,
    removeHeatingGeneration,
    dhwGenerationFields,
    appendDHWGeneration,
    removeDHWGeneration,
    heatingTransferFields,
    appendHeatingTransfer,
    removeHeatingTransfer,
    ventilationSystemFields,
    appendVentilationSystem,
    removeVentilationSystem,
    // Enum options
    orientationOptions,
    roofOrientationOptions,
    borderingTypeOptions,
    heatingGeneratorTypeOptions,
    dhwGeneratorTypeOptions,
    heatingTransferTypeOptions,
    ventilationTypeOptions,
  } = controller

  // Helper function to get page number from metadata for a given field path
  const getPageForField = (fieldPath: string): number | undefined => {
    if (!metadata) return undefined

    const parts = fieldPath.split('.')
    let current: unknown = metadata

    for (const part of parts) {
      // Check if part is a numeric index (for arrays)
      if (/^\d+$/.test(part)) {
        const index = parseInt(part, 10)
        if (Array.isArray(current) && index < current.length) {
          current = current[index]
        } else {
          return undefined
        }
      } else if (current && typeof current === 'object' && part in current) {
        current = (current as Record<string, unknown>)[part]
      } else {
        return undefined
      }
    }

    // Check if current has a page property
    if (current && typeof current === 'object' && 'page' in current) {
      return (current as { page: number }).page
    }

    return undefined
  }

  // Handle field focus to scroll PDF
  const handleFieldFocus = (fieldPath: string) => {
    if (!onFieldFocus) return
    const pageNumber = getPageForField(fieldPath)
    if (pageNumber) {
      onFieldFocus(pageNumber)
    }
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
        overflow: 'auto',
      }}
    >
      {/* Building Information Section */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '16px' }}>
          Gebäudeinformationen
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Controller
            name="buildingInformation.heatedVolumeInCbm"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Beheiztes Volumen (m³)"
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => onChange(parseFloat(e.target.value))}
                onFocus={() => handleFieldFocus('buildingInformation.heatedVolumeInCbm')}
                error={!!errors.buildingInformation?.heatedVolumeInCbm}
                helperText={getFieldErrorMessage(
                  errors.buildingInformation?.heatedVolumeInCbm,
                  'heatedVolumeInCbm'
                )}
                slotProps={{ htmlInput: { step: 'any', min: 0 } }}
              />
            )}
          />

          <Controller
            name="buildingInformation.usableSpaceInSqm"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Nutzfläche (m²)"
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => onChange(parseFloat(e.target.value))}
                onFocus={() => handleFieldFocus('buildingInformation.usableSpaceInSqm')}
                error={!!errors.buildingInformation?.usableSpaceInSqm}
                helperText={getFieldErrorMessage(
                  errors.buildingInformation?.usableSpaceInSqm,
                  'usableSpaceInSqm'
                )}
                slotProps={{ htmlInput: { step: 'any', min: 0 } }}
              />
            )}
          />

          <Controller
            name="buildingInformation.constructionYear"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Baujahr"
                type="number"
                fullWidth
                value={value}
                onChange={e => onChange(parseInt(e.target.value, 10))}
                onFocus={() => handleFieldFocus('buildingInformation.constructionYear')}
                error={!!errors.buildingInformation?.constructionYear}
                helperText={getFieldErrorMessage(
                  errors.buildingInformation?.constructionYear,
                  'constructionYear'
                )}
                slotProps={{ htmlInput: { step: 1 } }}
              />
            )}
          />

          <Controller
            name="buildingInformation.numberOfResidentialUnits"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Anzahl der Wohneinheiten"
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => onChange(parseInt(e.target.value, 10))}
                onFocus={() => handleFieldFocus('buildingInformation.numberOfResidentialUnits')}
                error={!!errors.buildingInformation?.numberOfResidentialUnits}
                helperText={getFieldErrorMessage(
                  errors.buildingInformation?.numberOfResidentialUnits,
                  'numberOfResidentialUnits'
                )}
                slotProps={{ htmlInput: { step: 1, min: 0 } }}
              />
            )}
          />

          <Controller
            name="buildingInformation.numberOfFloors"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Anzahl der Stockwerke"
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => onChange(parseInt(e.target.value, 10))}
                onFocus={() => handleFieldFocus('buildingInformation.numberOfFloors')}
                error={!!errors.buildingInformation?.numberOfFloors}
                helperText={getFieldErrorMessage(
                  errors.buildingInformation?.numberOfFloors,
                  'numberOfFloors'
                )}
                slotProps={{ htmlInput: { step: 1, min: 0 } }}
              />
            )}
          />

          <Controller
            name="buildingInformation.meanFloorHeightInCm"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Mittlere Geschosshöhe (cm)"
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => onChange(parseFloat(e.target.value))}
                onFocus={() => handleFieldFocus('buildingInformation.meanFloorHeightInCm')}
                error={!!errors.buildingInformation?.meanFloorHeightInCm}
                helperText={getFieldErrorMessage(
                  errors.buildingInformation?.meanFloorHeightInCm,
                  'meanFloorHeightInCm'
                )}
                slotProps={{ htmlInput: { step: 'any', min: 0 } }}
              />
            )}
          />
        </Box>
      </Box>

      {/* Energetic Values Section */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '16px' }}>
          Energetische Werte
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Controller
            name="energeticValues.finalEnergyDemandInKwh"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Endenergiebedarf (kWh)"
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => onChange(parseFloat(e.target.value))}
                onFocus={() => handleFieldFocus('energeticValues.finalEnergyDemandInKwh')}
                error={!!errors.energeticValues?.finalEnergyDemandInKwh}
                helperText={getFieldErrorMessage(
                  errors.energeticValues?.finalEnergyDemandInKwh,
                  'finalEnergyDemandInKwh'
                )}
                slotProps={{ htmlInput: { step: 'any', min: 0 } }}
              />
            )}
          />

          <Controller
            name="energeticValues.auxEnergyDemandInKwh"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Hilfsenergiebedarf (kWh)"
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => onChange(parseFloat(e.target.value))}
                onFocus={() => handleFieldFocus('energeticValues.auxEnergyDemandInKwh')}
                error={!!errors.energeticValues?.auxEnergyDemandInKwh}
                helperText={getFieldErrorMessage(
                  errors.energeticValues?.auxEnergyDemandInKwh,
                  'auxEnergyDemandInKwh'
                )}
                slotProps={{ htmlInput: { step: 'any', min: 0 } }}
              />
            )}
          />

          <Controller
            name="energeticValues.energyConsumptionInKwh"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Energieverbrauch (kWh)"
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => onChange(parseFloat(e.target.value))}
                onFocus={() => handleFieldFocus('energeticValues.energyConsumptionInKwh')}
                error={!!errors.energeticValues?.energyConsumptionInKwh}
                helperText={getFieldErrorMessage(
                  errors.energeticValues?.energyConsumptionInKwh,
                  'energyConsumptionInKwh'
                )}
                slotProps={{ htmlInput: { step: 'any', min: 0 } }}
              />
            )}
          />

          <Controller
            name="energeticValues.primaryEnergyDemandInKwh"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Primärenergiebedarf (kWh)"
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => onChange(parseFloat(e.target.value))}
                onFocus={() => handleFieldFocus('energeticValues.primaryEnergyDemandInKwh')}
                error={!!errors.energeticValues?.primaryEnergyDemandInKwh}
                helperText={getFieldErrorMessage(
                  errors.energeticValues?.primaryEnergyDemandInKwh,
                  'primaryEnergyDemandInKwh'
                )}
                slotProps={{ htmlInput: { step: 'any', min: 0 } }}
              />
            )}
          />

          <Controller
            name="energeticValues.transmissionHeatLossInWPerK"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Transmissionswärmeverlust (W/K)"
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => onChange(parseFloat(e.target.value))}
                onFocus={() => handleFieldFocus('energeticValues.transmissionHeatLossInWPerK')}
                error={!!errors.energeticValues?.transmissionHeatLossInWPerK}
                helperText={getFieldErrorMessage(
                  errors.energeticValues?.transmissionHeatLossInWPerK,
                  'transmissionHeatLossInWPerK'
                )}
                slotProps={{ htmlInput: { step: 'any', min: 0 } }}
              />
            )}
          />

          <Controller
            name="energeticValues.equivalentCo2EmissionsInTons"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <TextField
                {...field}
                label="Äquivalente CO₂ Emissionen (t/a)"
                type="number"
                fullWidth
                value={value ?? ''}
                onChange={e => onChange(parseFloat(e.target.value))}
                onFocus={() => handleFieldFocus('energeticValues.equivalentCo2EmissionsInTons')}
                error={!!errors.energeticValues?.equivalentCo2EmissionsInTons}
                helperText={getFieldErrorMessage(
                  errors.energeticValues?.equivalentCo2EmissionsInTons,
                  'equivalentCo2EmissionsInTons'
                )}
                slotProps={{ htmlInput: { step: 'any', min: 0 } }}
              />
            )}
          />
        </Box>
      </Box>

      {/* Substance Section */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '16px' }}>
          Bausubstanz
        </Typography>

        {/* Exterior Wall Components */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Außenwände
          </Typography>
          {exteriorWallFields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #E1E1E4',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Controller
                  name={`substance.exteriorWallComponents.${index}.orientation`}
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      sx={{ flex: 1 }}
                      error={!!errors.substance?.exteriorWallComponents?.[index]?.orientation}
                    >
                      <InputLabel>Ausrichtung</InputLabel>
                      <Select
                        {...field}
                        label="Ausrichtung"
                        value={field.value ?? ''}
                        onFocus={() =>
                          handleFieldFocus(`substance.exteriorWallComponents.${index}`)
                        }
                      >
                        {orientationOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.substance?.exteriorWallComponents?.[index]?.orientation && (
                        <FormHelperText>
                          {getArrayFieldErrorMessage(
                            errors,
                            'substance.exteriorWallComponents',
                            index,
                            'orientation'
                          )}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name={`substance.exteriorWallComponents.${index}.areaInSqm`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="Fläche (m²)"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.exteriorWallComponents.${index}`)}
                      error={!!errors.substance?.exteriorWallComponents?.[index]?.areaInSqm}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.exteriorWallComponents',
                        index,
                        'areaInSqm'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name={`substance.exteriorWallComponents.${index}.uValue`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="U-Wert"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.exteriorWallComponents.${index}`)}
                      error={!!errors.substance?.exteriorWallComponents?.[index]?.uValue}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.exteriorWallComponents',
                        index,
                        'uValue'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => removeExteriorWall(index)}
                  size="small"
                  sx={{ marginTop: '4px' }}
                >
                  <Trash01Icon style={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = exteriorWallFields.length
              appendExteriorWall({ uValue: 0, areaInSqm: 0, orientation: 'north' })
              setTimeout(() => {
                trigger(`substance.exteriorWallComponents.${newIndex}`)
              }, 0)
            }}
          >
            Außenwand hinzufügen
          </Button>
        </Box>

        {/* Roof Components */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Dach
          </Typography>
          {roofFields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #E1E1E4',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Controller
                  name={`substance.roofComponents.${index}.orientation`}
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      sx={{ flex: 1 }}
                      error={!!errors.substance?.roofComponents?.[index]?.orientation}
                    >
                      <InputLabel>Ausrichtung</InputLabel>
                      <Select
                        {...field}
                        label="Ausrichtung"
                        value={field.value ?? ''}
                        onFocus={() => handleFieldFocus(`substance.roofComponents.${index}`)}
                      >
                        {roofOrientationOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.substance?.roofComponents?.[index]?.orientation && (
                        <FormHelperText>
                          {getArrayFieldErrorMessage(
                            errors,
                            'substance.roofComponents',
                            index,
                            'orientation'
                          )}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name={`substance.roofComponents.${index}.areaInSqm`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="Fläche (m²)"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.roofComponents.${index}`)}
                      error={!!errors.substance?.roofComponents?.[index]?.areaInSqm}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.roofComponents',
                        index,
                        'areaInSqm'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name={`substance.roofComponents.${index}.uValue`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="U-Wert"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.roofComponents.${index}`)}
                      error={!!errors.substance?.roofComponents?.[index]?.uValue}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.roofComponents',
                        index,
                        'uValue'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => removeRoof(index)}
                  size="small"
                  sx={{ marginTop: '4px' }}
                >
                  <Trash01Icon style={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = roofFields.length
              appendRoof({ uValue: 0, areaInSqm: 0, orientation: 'north' })
              setTimeout(() => {
                trigger(`substance.roofComponents.${newIndex}`)
              }, 0)
            }}
          >
            Dach hinzufügen
          </Button>
        </Box>

        {/* Top Floor Ceiling Components */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Oberste Geschossdecke
          </Typography>
          {topFloorCeilingFields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #E1E1E4',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Controller
                  name={`substance.topFloorCeilingComponents.${index}.areaInSqm`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="Fläche (m²)"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() =>
                        handleFieldFocus(`substance.topFloorCeilingComponents.${index}`)
                      }
                      error={!!errors.substance?.topFloorCeilingComponents?.[index]?.areaInSqm}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.topFloorCeilingComponents',
                        index,
                        'areaInSqm'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name={`substance.topFloorCeilingComponents.${index}.uValue`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="U-Wert"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() =>
                        handleFieldFocus(`substance.topFloorCeilingComponents.${index}`)
                      }
                      error={!!errors.substance?.topFloorCeilingComponents?.[index]?.uValue}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.topFloorCeilingComponents',
                        index,
                        'uValue'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => removeTopFloorCeiling(index)}
                  size="small"
                  sx={{ marginTop: '4px' }}
                >
                  <Trash01Icon style={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = topFloorCeilingFields.length
              appendTopFloorCeiling({ uValue: 0, areaInSqm: 0 })
              setTimeout(() => {
                trigger(`substance.topFloorCeilingComponents.${newIndex}`)
              }, 0)
            }}
          >
            Oberste Geschossdecke hinzufügen
          </Button>
        </Box>

        {/* Basement Ceiling Components */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Kellerdecke
          </Typography>
          {basementCeilingFields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #E1E1E4',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Controller
                  name={`substance.basementCeilingComponents.${index}.areaInSqm`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="Fläche (m²)"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() =>
                        handleFieldFocus(`substance.basementCeilingComponents.${index}`)
                      }
                      error={!!errors.substance?.basementCeilingComponents?.[index]?.areaInSqm}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.basementCeilingComponents',
                        index,
                        'areaInSqm'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name={`substance.basementCeilingComponents.${index}.uValue`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="U-Wert"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() =>
                        handleFieldFocus(`substance.basementCeilingComponents.${index}`)
                      }
                      error={!!errors.substance?.basementCeilingComponents?.[index]?.uValue}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.basementCeilingComponents',
                        index,
                        'uValue'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => removeBasementCeiling(index)}
                  size="small"
                  sx={{ marginTop: '4px' }}
                >
                  <Trash01Icon style={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = basementCeilingFields.length
              appendBasementCeiling({ uValue: 0, areaInSqm: 0 })
              setTimeout(() => {
                trigger(`substance.basementCeilingComponents.${newIndex}`)
              }, 0)
            }}
          >
            Kellerdecke hinzufügen
          </Button>
        </Box>

        {/* Floor Plate Components */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Bodenplatte
          </Typography>
          {floorPlateFields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #E1E1E4',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Controller
                  name={`substance.floorPlateComponents.${index}.borderingType`}
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      sx={{ flex: 1 }}
                      error={!!errors.substance?.floorPlateComponents?.[index]?.borderingType}
                    >
                      <InputLabel>Angrenzend</InputLabel>
                      <Select
                        {...field}
                        label="Angrenzend"
                        onFocus={() => handleFieldFocus(`substance.floorPlateComponents.${index}`)}
                        value={field.value ?? ''}
                      >
                        {borderingTypeOptions
                          .filter(option => option.value !== 'toUnheated')
                          .map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                      </Select>
                      {errors.substance?.floorPlateComponents?.[index]?.borderingType && (
                        <FormHelperText>
                          {getArrayFieldErrorMessage(
                            errors,
                            'substance.floorPlateComponents',
                            index,
                            'borderingType'
                          )}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name={`substance.floorPlateComponents.${index}.areaInSqm`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="Fläche (m²)"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.floorPlateComponents.${index}`)}
                      error={!!errors.substance?.floorPlateComponents?.[index]?.areaInSqm}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.floorPlateComponents',
                        index,
                        'areaInSqm'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name={`substance.floorPlateComponents.${index}.uValue`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="U-Wert"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.floorPlateComponents.${index}`)}
                      error={!!errors.substance?.floorPlateComponents?.[index]?.uValue}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.floorPlateComponents',
                        index,
                        'uValue'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => removeFloorPlate(index)}
                  size="small"
                  sx={{ marginTop: '4px' }}
                >
                  <Trash01Icon style={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = floorPlateFields.length
              appendFloorPlate({ uValue: 0, areaInSqm: 0 })
              setTimeout(() => {
                trigger(`substance.floorPlateComponents.${newIndex}`)
              }, 0)
            }}
          >
            Bodenplatte hinzufügen
          </Button>
        </Box>

        {/* Basement Exterior Wall Components */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Kelleraußenwände
          </Typography>
          {basementExteriorWallFields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #E1E1E4',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Controller
                  name={`substance.basementExteriorWallComponents.${index}.orientation`}
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      sx={{ flex: 1 }}
                      error={
                        !!errors.substance?.basementExteriorWallComponents?.[index]?.orientation
                      }
                    >
                      <InputLabel>Ausrichtung</InputLabel>
                      <Select
                        {...field}
                        label="Ausrichtung"
                        value={field.value ?? ''}
                        onFocus={() =>
                          handleFieldFocus(`substance.basementExteriorWallComponents.${index}`)
                        }
                      >
                        {orientationOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.substance?.basementExteriorWallComponents?.[index]?.orientation && (
                        <FormHelperText>
                          {getArrayFieldErrorMessage(
                            errors,
                            'substance.basementExteriorWallComponents',
                            index,
                            'orientation'
                          )}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name={`substance.basementExteriorWallComponents.${index}.borderingType`}
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      sx={{ flex: 1 }}
                      error={
                        !!errors.substance?.basementExteriorWallComponents?.[index]?.borderingType
                      }
                    >
                      <InputLabel>Angrenzend</InputLabel>
                      <Select
                        {...field}
                        label="Angrenzend"
                        value={field.value ?? ''}
                        onChange={e => {
                          field.onChange(e)
                          // Trigger validation on orientation field when borderingType changes
                          trigger(`substance.basementExteriorWallComponents.${index}.orientation`)
                        }}
                      >
                        {borderingTypeOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.substance?.basementExteriorWallComponents?.[index]?.borderingType && (
                        <FormHelperText>
                          {getArrayFieldErrorMessage(
                            errors,
                            'substance.basementExteriorWallComponents',
                            index,
                            'borderingType'
                          )}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name={`substance.basementExteriorWallComponents.${index}.areaInSqm`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="Fläche (m²)"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() =>
                        handleFieldFocus(`substance.basementExteriorWallComponents.${index}`)
                      }
                      error={!!errors.substance?.basementExteriorWallComponents?.[index]?.areaInSqm}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.basementExteriorWallComponents',
                        index,
                        'areaInSqm'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name={`substance.basementExteriorWallComponents.${index}.uValue`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="U-Wert"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() =>
                        handleFieldFocus(`substance.basementExteriorWallComponents.${index}`)
                      }
                      error={!!errors.substance?.basementExteriorWallComponents?.[index]?.uValue}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.basementExteriorWallComponents',
                        index,
                        'uValue'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => removeBasementExteriorWall(index)}
                  size="small"
                  sx={{ marginTop: '4px' }}
                >
                  <Trash01Icon style={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = basementExteriorWallFields.length
              appendBasementExteriorWall({
                uValue: 0,
                areaInSqm: 0,
                orientation: undefined,
                borderingType: 'toSoil',
              })
              setTimeout(() => {
                trigger(`substance.basementExteriorWallComponents.${newIndex}`)
              }, 0)
            }}
          >
            Kelleraußenwand hinzufügen
          </Button>
        </Box>

        {/* Window Components */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Fenster
          </Typography>
          {windowFields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #E1E1E4',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Controller
                  name={`substance.windowComponents.${index}.orientation`}
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      sx={{ flex: 1 }}
                      error={!!errors.substance?.windowComponents?.[index]?.orientation}
                    >
                      <InputLabel>Ausrichtung</InputLabel>
                      <Select
                        {...field}
                        label="Ausrichtung"
                        value={field.value ?? ''}
                        onFocus={() => handleFieldFocus(`substance.windowComponents.${index}`)}
                      >
                        {orientationOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.substance?.windowComponents?.[index]?.orientation && (
                        <FormHelperText>
                          {getArrayFieldErrorMessage(
                            errors,
                            'substance.windowComponents',
                            index,
                            'orientation'
                          )}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name={`substance.windowComponents.${index}.areaInSqm`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="Fläche (m²)"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.windowComponents.${index}`)}
                      error={!!errors.substance?.windowComponents?.[index]?.areaInSqm}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.windowComponents',
                        index,
                        'areaInSqm'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name={`substance.windowComponents.${index}.uValue`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="U-Wert"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.windowComponents.${index}`)}
                      error={!!errors.substance?.windowComponents?.[index]?.uValue}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.windowComponents',
                        index,
                        'uValue'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => removeWindow(index)}
                  size="small"
                  sx={{ marginTop: '4px' }}
                >
                  <Trash01Icon style={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = windowFields.length
              appendWindow({ uValue: 0, areaInSqm: 0, orientation: 'north' })
              setTimeout(() => {
                trigger(`substance.windowComponents.${newIndex}`)
              }, 0)
            }}
          >
            Fenster hinzufügen
          </Button>
        </Box>

        {/* Door Components */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Türen
          </Typography>
          {doorFields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #E1E1E4',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Controller
                  name={`substance.doorComponents.${index}.orientation`}
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      sx={{ flex: 1 }}
                      error={!!errors.substance?.doorComponents?.[index]?.orientation}
                    >
                      <InputLabel>Ausrichtung</InputLabel>
                      <Select
                        {...field}
                        label="Ausrichtung"
                        value={field.value ?? ''}
                        onFocus={() => handleFieldFocus(`substance.doorComponents.${index}`)}
                      >
                        {orientationOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.substance?.doorComponents?.[index]?.orientation && (
                        <FormHelperText>
                          {getArrayFieldErrorMessage(
                            errors,
                            'substance.doorComponents',
                            index,
                            'orientation'
                          )}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name={`substance.doorComponents.${index}.areaInSqm`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="Fläche (m²)"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.doorComponents.${index}`)}
                      error={!!errors.substance?.doorComponents?.[index]?.areaInSqm}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.doorComponents',
                        index,
                        'areaInSqm'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name={`substance.doorComponents.${index}.uValue`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="U-Wert"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.doorComponents.${index}`)}
                      error={!!errors.substance?.doorComponents?.[index]?.uValue}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.doorComponents',
                        index,
                        'uValue'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => removeDoor(index)}
                  size="small"
                  sx={{ marginTop: '4px' }}
                >
                  <Trash01Icon style={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = doorFields.length
              appendDoor({ uValue: 0, areaInSqm: 0, orientation: 'north' })
              setTimeout(() => {
                trigger(`substance.doorComponents.${newIndex}`)
              }, 0)
            }}
          >
            Tür hinzufügen
          </Button>
        </Box>

        {/* Roof Window Components */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Dachfenster
          </Typography>
          {roofWindowFields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #E1E1E4',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Controller
                  name={`substance.roofWindowComponents.${index}.orientation`}
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      sx={{ flex: 1 }}
                      error={!!errors.substance?.roofWindowComponents?.[index]?.orientation}
                    >
                      <InputLabel>Ausrichtung</InputLabel>
                      <Select
                        {...field}
                        label="Ausrichtung"
                        value={field.value ?? ''}
                        onFocus={() => handleFieldFocus(`substance.roofWindowComponents.${index}`)}
                      >
                        {roofOrientationOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.substance?.roofWindowComponents?.[index]?.orientation && (
                        <FormHelperText>
                          {getArrayFieldErrorMessage(
                            errors,
                            'substance.roofWindowComponents',
                            index,
                            'orientation'
                          )}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name={`substance.roofWindowComponents.${index}.areaInSqm`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="Fläche (m²)"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.roofWindowComponents.${index}`)}
                      error={!!errors.substance?.roofWindowComponents?.[index]?.areaInSqm}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.roofWindowComponents',
                        index,
                        'areaInSqm'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Controller
                  name={`substance.roofWindowComponents.${index}.uValue`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="U-Wert"
                      type="number"
                      size="small"
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() => handleFieldFocus(`substance.roofWindowComponents.${index}`)}
                      error={!!errors.substance?.roofWindowComponents?.[index]?.uValue}
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'substance.roofWindowComponents',
                        index,
                        'uValue'
                      )}
                      slotProps={{ htmlInput: { step: 'any', min: 0 } }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <IconButton
                  onClick={() => removeRoofWindow(index)}
                  size="small"
                  sx={{ marginTop: '4px' }}
                >
                  <Trash01Icon style={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = roofWindowFields.length
              appendRoofWindow({ uValue: 0, areaInSqm: 0, orientation: 'north' })
              setTimeout(() => {
                trigger(`substance.roofWindowComponents.${newIndex}`)
              }, 0)
            }}
          >
            Dachfenster hinzufügen
          </Button>
        </Box>
      </Box>

      {/* System Technology Section */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '16px' }}>
          Anlagentechnik
        </Typography>

        {/* Heating Generation */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Wärmeerzeugung
          </Typography>
          {heatingGenerationFields.map((field, index) => (
            <HeatingGenerationField
              key={field.id}
              control={control}
              errors={errors}
              index={index}
              heatingGeneratorTypeOptions={heatingGeneratorTypeOptions}
              setValue={setValue}
              onRemove={() => removeHeatingGeneration(index)}
              onFieldFocus={handleFieldFocus}
            />
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = heatingGenerationFields.length
              appendHeatingGeneration({
                generatorType: 'boiler',
                energySource: 'gas',
                constructionYear: new Date().getFullYear(),
                coverageInPct: 100,
              })
              setTimeout(() => {
                trigger(`systemTechnology.heatingGeneration.${newIndex}`)
              }, 0)
            }}
          >
            Wärmeerzeugung hinzufügen
          </Button>
        </Box>

        {/* Drinking Hot Water Generation */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Warmwassererzeugung
          </Typography>
          {dhwGenerationFields.map((field, index) => (
            <DHWGenerationField
              key={field.id}
              control={control}
              errors={errors}
              index={index}
              dhwGeneratorTypeOptions={dhwGeneratorTypeOptions}
              setValue={setValue}
              onRemove={() => removeDHWGeneration(index)}
              onFieldFocus={handleFieldFocus}
            />
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = dhwGenerationFields.length
              appendDHWGeneration({
                generatorType: 'combinedWithHeating',
                energySource: 'gas',
                constructionYear: new Date().getFullYear(),
                coverageInPct: 100,
              })
              setTimeout(() => {
                trigger(`systemTechnology.drinkingHotWaterGeneration.${newIndex}`)
              }, 0)
            }}
          >
            Warmwassererzeugung hinzufügen
          </Button>
        </Box>

        {/* Heating Transfer */}
        <Box sx={{ marginBottom: '24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Wärmeübertragung
          </Typography>
          {heatingTransferFields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #E1E1E4',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Controller
                  name={`systemTechnology.heatingTransfer.${index}.type`}
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      sx={{ flex: 1 }}
                      error={!!errors.systemTechnology?.heatingTransfer?.[index]?.type}
                    >
                      <InputLabel>Typ</InputLabel>
                      <Select
                        {...field}
                        label="Typ"
                        value={field.value ?? ''}
                        onFocus={() =>
                          handleFieldFocus(`systemTechnology.heatingTransfer.${index}`)
                        }
                      >
                        {heatingTransferTypeOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.systemTechnology?.heatingTransfer?.[index]?.type && (
                        <FormHelperText>
                          {getArrayFieldErrorMessage(
                            errors,
                            'systemTechnology.heatingTransfer',
                            index,
                            'type'
                          )}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <IconButton
                  onClick={() => removeHeatingTransfer(index)}
                  size="small"
                  sx={{ marginTop: '4px' }}
                >
                  <Trash01Icon style={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = heatingTransferFields.length
              appendHeatingTransfer({ type: 'radiators' })
              setTimeout(() => {
                trigger(`systemTechnology.heatingTransfer.${newIndex}`)
              }, 0)
            }}
          >
            Wärmeübertragung hinzufügen
          </Button>
        </Box>

        {/* Ventilation System */}
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, marginBottom: '12px' }}>
            Lüftungssystem (Optional)
          </Typography>
          {ventilationSystemFields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #E1E1E4',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <Controller
                  name={`systemTechnology.ventilationSystem.${index}.ventilationType`}
                  control={control}
                  render={({ field }) => (
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Lüftungstyp</InputLabel>
                      <Select
                        {...field}
                        label="Lüftungstyp"
                        value={field.value ?? ''}
                        onFocus={() =>
                          handleFieldFocus(`systemTechnology.ventilationSystem.${index}`)
                        }
                      >
                        {ventilationTypeOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.systemTechnology?.ventilationSystem?.[index]?.ventilationType && (
                        <FormHelperText error>
                          {getArrayFieldErrorMessage(
                            errors,
                            'systemTechnology.ventilationSystem',
                            index,
                            'ventilationType'
                          )}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
                <Controller
                  name={`systemTechnology.ventilationSystem.${index}.heatRecoveryRateInPct`}
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <TextField
                      {...field}
                      label="Wärmerückgewinnungsrate (%)"
                      type="number"
                      size="small"
                      sx={{ flex: 1 }}
                      value={value ?? ''}
                      onChange={e => onChange(parseFloat(e.target.value))}
                      onFocus={() =>
                        handleFieldFocus(`systemTechnology.ventilationSystem.${index}`)
                      }
                      slotProps={{ htmlInput: { step: 'any', min: 0, max: 100 } }}
                      error={
                        !!errors.systemTechnology?.ventilationSystem?.[index]?.heatRecoveryRateInPct
                      }
                      helperText={getArrayFieldErrorMessage(
                        errors,
                        'systemTechnology.ventilationSystem',
                        index,
                        'heatRecoveryRateInPct'
                      )}
                    />
                  )}
                />
                <IconButton
                  onClick={() => removeVentilationSystem(index)}
                  size="small"
                  sx={{ marginTop: '4px' }}
                >
                  <Trash01Icon style={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newIndex = ventilationSystemFields.length
              appendVentilationSystem({
                ventilationType: 'naturalVentilation',
                heatRecoveryRateInPct: 0,
              })
              setTimeout(() => {
                trigger(`systemTechnology.ventilationSystem.${newIndex}`)
              }, 0)
            }}
          >
            Lüftungssystem hinzufügen
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default ISFPUploadForm
